import { useMemo } from 'react';

import {
  buildGlobalTotalsByType,
  getFilteredMeterChartDataByVisibleKeys,
  getFilteredTradeChartDataByVisibleKeys,
} from 'src/util/chartFiltering';
import { DEFAULT_MAX_VISIBLE_CARDS_COUNT } from 'src/util/constants';

// Stable empty Set returned when there are no selected keys, preventing
// unnecessary re-evaluations of the filteredChartData memo on selection changes
// in single-page mode.
const EMPTY_TRADE_TYPES = new Set();

/**
 * Computes visible cards, the visible key set, and filtered chart data for
 * paginated dashboard charts. Handles pinning of selected-but-off-page cards
 * so they remain visible in their own dedicated series rather than being
 * merged into the "Other" bucket.
 *
 * Shared between PortfolioShowChart and PropertyShowChart. Any change to the
 * per-point data-shape contract in chartFiltering.js must be reflected in
 * both callers.
 * @param {object} params
 * @param {Array} params.pageCards - Cards for the current page from buildPaginationMetadata.
 * @param {string[]} params.finalSelectedKeys - Selected card keys for the active chart view.
 * @param {number} params.totalPages - Total page count from buildPaginationMetadata.
 * @param {Array} params.cardsData - Full list of all cards across all pages.
 * @param {object} params.chartData - Raw chart data (trade or meter shape).
 * @param {boolean} params.meterView - Whether the active source is meter/historian view.
 * @param {string} params.meterOtherLabel - Translated label for the aggregated meter
 *   "Other" entry (used only when `meterView` is true).
 * @param {Function} params.getLabelForOtherSubSeriesFunc - Callback
 *   `(tradeType, alsoVisible) => string` returning the translated label for each
 *   typed trade "Other" sub-series (used only when `meterView` is false).
 * @returns {{ visibleCards: Array, visibleKeys: Set<string>|null, filteredChartData: object }}
 * Filtered cards and chart data for the current page.
 */
const useFilteredChartData = ({
  pageCards,
  finalSelectedKeys,
  totalPages,
  cardsData,
  chartData,
  meterView,
  meterOtherLabel,
  getLabelForOtherSubSeriesFunc,
}) => {
  // Build the set of trade types that the user has explicitly selected across all pages.
  // This is used so that hidden "Other" sub-series whose trade type matches a selected
  // card are always labelled "Other {type}" — even when the selected card only lives on
  // a different page and is therefore absent from the current page's visibleSeries.
  const selectedTradeTypes = useMemo(() => {
    if (finalSelectedKeys.length === 0) return EMPTY_TRADE_TYPES;
    const selectedKeySet = new Set(finalSelectedKeys);
    const types = new Set();
    cardsData.forEach((c) => {
      if (selectedKeySet.has(c.key) && c.tradeType) {
        types.add(c.tradeType);
      }
    });
    return types;
  }, [finalSelectedKeys, cardsData]);

  // When on a page that does not natively contain the selected card, prepend it so it
  // remains visible and retains its own dedicated bar (not merged into "Other").
  const visibleCards = useMemo(() => {
    if (finalSelectedKeys.length === 0 || totalPages <= 1) {
      return pageCards;
    }
    const pageKeySet = new Set(pageCards.map((c) => c.key));
    const selectedKeySet = new Set(finalSelectedKeys);
    const pinnedCards = cardsData.filter(
      (c) => selectedKeySet.has(c.key) && !pageKeySet.has(c.key),
    );
    // Cap the combined set to DEFAULT_MAX_VISIBLE_CARDS_COUNT so the chart
    // never renders more series than the page size, regardless of how many
    // selected-but-off-page cards exist.
    const combined = pinnedCards.length > 0 ? [...pinnedCards, ...pageCards] : pageCards;
    return combined.slice(0, DEFAULT_MAX_VISIBLE_CARDS_COUNT);
  }, [pageCards, finalSelectedKeys, totalPages, cardsData]);

  const visibleKeys = useMemo(
    () => (totalPages > 1 ? new Set(visibleCards.map((c) => c.key)) : null),
    [totalPages, visibleCards],
  );

  // Pre-compute per-type global totals from all series, independent of pagination and
  // card selection. This memo only invalidates when the underlying data changes, so the
  // expensive O(all × timestamps) aggregation is paid once per period rather than on
  // every page turn or selection toggle.
  // Only computed for trade view; meter view uses a separate "Other" path.
  const globalTotalsByType = useMemo(() => {
    if (!chartData || meterView) return null;
    return buildGlobalTotalsByType(chartData);
  }, [chartData, meterView]);

  const filteredChartData = useMemo(() => {
    if (!visibleKeys) {
      return chartData;
    }
    if (meterView) {
      return getFilteredMeterChartDataByVisibleKeys(chartData, visibleKeys, meterOtherLabel);
    }
    return getFilteredTradeChartDataByVisibleKeys(
      chartData,
      visibleKeys,
      getLabelForOtherSubSeriesFunc,
      selectedTradeTypes,
      globalTotalsByType,
    );
  }, [
    chartData, meterView, visibleKeys, meterOtherLabel,
    getLabelForOtherSubSeriesFunc, selectedTradeTypes, globalTotalsByType,
  ]);

  return { visibleCards, visibleKeys, filteredChartData };
};

export default useFilteredChartData;
